import type { AuthErrorCode } from './types';

const CODE_MAP: Record<string, AuthErrorCode> = {
  NotAuthorizedException: 'INVALID_CREDENTIALS',
  UserNotFoundException: 'USER_NOT_FOUND',
  PasswordResetRequiredException: 'PASSWORD_RESET_REQUIRED',
  CodeMismatchException: 'CODE_MISMATCH',
  ExpiredCodeException: 'CODE_EXPIRED',
  InvalidPasswordException: 'INVALID_PASSWORD',
  InvalidParameterException: 'INVALID_PASSWORD',
  LimitExceededException: 'TOO_MANY_ATTEMPTS',
  TooManyRequestsException: 'TOO_MANY_ATTEMPTS',
  NetworkError: 'NETWORK',
};

/**
 * An authentication failure with a stable code for the screens and the raw Cognito error kept as
 * `cause` for logs. Cognito's `NotAuthorizedException` covers wrong password and disabled user
 * alike, on purpose: the screen never reveals which.
 */
export class AuthError extends Error {
  /**
   * Stable code.
   */
  public readonly code: AuthErrorCode;

  constructor(code: AuthErrorCode, message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'AuthError';
    this.code = code;
  }
}

/**
 * Maps whatever Cognito's SDK throws to an `AuthError`.
 *
 * @param {unknown} raw -- The rejection value from the SDK callback.
 * @returns The mapped error.
 */
export const toAuthError = (raw: unknown): AuthError => {
  const cognitoCode =
    typeof raw === 'object' && raw !== null && 'code' in raw
      ? String((raw as { code: unknown }).code)
      : '';
  const message = raw instanceof Error ? raw.message : 'Authentication failed';
  return new AuthError(CODE_MAP[cognitoCode] ?? 'UNKNOWN', message, raw);
};
